/**
 * Learn more about deep linking with React Navigation
 * https://reactnavigation.org/docs/deep-linking
 * https://reactnavigation.org/docs/configuring-links
 */

import { LinkingOptions } from "@react-navigation/native"
import * as Linking from "expo-linking"
import * as Notifications from "expo-notifications"
import { NotificationResponse } from "expo-notifications"

import { RootStackParamList } from "src/types.d"
import Constants from "expo-constants"
import { Platform } from "react-native"
import { getAsync } from "src/storage/secureStorage/getAsync"
import { setAsync } from "src/storage/secureStorage/setAsync"

const prefix = Linking.createURL("/")
const prefix_link = `https://${Constants.expoConfig?.extra?.deepLinkDomain}/dlink`

interface iosPushPayload {
  payload: {
    aps: any
    metadeta: any
    urlScheme: string
    url?: string
  }
  class: string
  type: string
}

interface androidPushPayload {
  payload: {
    notification: any
    collapse_key: any
    priority: string
    data: {
      urlScheme: string
      metadata: string
    }
  }
  remoteMessage: {
    notification: any
    collapse_key: any
    priority: string
    data: {
      urlScheme: string
      metadata: string
      url?: string
    }
  }
  class: string
  type: string
}

const linking: LinkingOptions<RootStackParamList> = {
  prefixes: [prefix, prefix_link],
  config: {
    screens: {
      Root: {
        screens: {
          NotificationTab: "notifications",
          ContactNetworkDrawer: {
            screens: {
              InboxScreen: "cn/inbox",
              OutboxScreen: "cn/outbox",
              DraftScreen: "cn/draft",
              TemplateScreen: "cn/template",
              TrashBoxScreen: "cn/trashbox",
              MigrationScreen: "cn/migration",
            },
          },
          OrganizationManagerTab: {
            screens: {
              OrganizationManager: "org",
              OrganizationDetail: "org/detail",
              OrganizationMemberGrade: "OrganizationMemberGrade",
              OrganizationMembers: "OrganizationMembers",
              OrganizationGroups: "OrganizationGroups",
              OrderDetail: "OrderDetail",
            },
          },
          SettingsTab: {
            screens: {
              SettingsTab: "settings",
              Information: "Information",
              AccountInfo: "AccountInfo",
              EditAccount: "EditAccount",
              Licenses: "Licenses",
              Inquiry: "Inquiry",
              InformationDetail: "InformationDetail",
              NotificationSettings: "NotificationSettings",
            },
          },
        },
      },
      CreateOrganizationStack: "org/create",
      JoinOrganization: "invitations/dialog",
      AuthorizationTop: "auth",
      SignupEmail: "auth/signup/email",
      SignupCode: "auth/signup/code",
      LoginEmail: "auth/login/email",
      LoginPhoneNumber: "auth/login/sms",
      LoginCode: "auth/login/code",
      AccountInfo: "settings/account",
      EditEmail: "settings/account/email",
      EditOrRegisterPhoneNumber: "settings/account/sms",
      ContactDetail: "contacts",
      ContactReplyThread: "replies",
      PendingInvitations: "invitations",
      InvitationMember: "org/:id/invite",
      DlinkRedirect: "dlink/*",
      NotFound: "*",
    },
  },
  // 初期設定のURL
  async getInitialURL() {
    const uri = (await Linking.getInitialURL()) ?? ""

    // Web: 通知機能はサポートされていないため、URIのみを返す
    if (Platform.OS === "web") {
      return uri
    }

    // iOS: タスクキル時の初期通知をチェック
    if (Platform.OS === "ios") {
      try {
        const lastResponse = await Notifications.getLastNotificationResponseAsync()
        if (lastResponse) {
          const trigger_ios = lastResponse?.notification?.request?.trigger as iosPushPayload
          const urlScheme = trigger_ios?.payload?.urlScheme

          if (urlScheme && urlScheme !== "") {
            return `${prefix}${urlScheme}`
          }
        }
      } catch (error) {
        console.error("[LinkingConfiguration] Failed to get iOS initial notification:", error)
      }
    }

    // Android: 保存されたurlSchemeをチェック
    if (Platform.OS === "android") {
      try {
        const urlScheme = (await getAsync("urlScheme")) ?? ""
        if (urlScheme !== "") {
          setAsync("urlScheme", "")
          if (uri === "") return `${prefix}${urlScheme}`
        }
      } catch (error) {
        console.error("[LinkingConfiguration] Failed to get Android urlScheme:", error)
      }
    }
    return uri
  },
  // push通知から取得
  subscribe(listener) {
    const onReceiveURL = async ({ url }: { url: string }) => {
      listener(url)
    }

    const linkingSubscription = Linking.addEventListener("url", onReceiveURL)

    // Web環境では通知機能をスキップ
    if (Platform.OS === "web") {
      return () => {
        linkingSubscription.remove()
      }
    }

    // ユーザーが通知をタップまたは操作したときに発生します
    // （アプリがフォアグラウンド、バックグラウンド、またはキルされたときに動作します）
    let notificationSubscription: ReturnType<typeof Notifications.addNotificationResponseReceivedListener> | null = null

    try {
      notificationSubscription = Notifications.addNotificationResponseReceivedListener((response: NotificationResponse) => {
        const trigger_ios = response?.notification?.request?.trigger as iosPushPayload
        const trigger_android = response?.notification?.request?.trigger as androidPushPayload

        const urlScheme =
          trigger_ios?.payload?.urlScheme ||
          trigger_android?.payload?.data?.urlScheme ||
          trigger_android?.remoteMessage?.data?.urlScheme

        if (urlScheme && urlScheme !== "") {
          const navigateToScheme = async () => {
            if (response.actionIdentifier === Notifications.DEFAULT_ACTION_IDENTIFIER) {
              await new Promise((resolve) => setTimeout(resolve, 500))
            }
            listener(`${prefix}${urlScheme}`)
          }
          navigateToScheme()
        }
      })
    } catch (error) {
      console.error("[LinkingConfiguration] Failed to add notification listener:", error)
    }

    return () => {
      linkingSubscription.remove()
      if (notificationSubscription) {
        notificationSubscription.remove()
      }
    }
  },
}

export default linking
